import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldCheck, Undo2, Building2, CheckCircle2, Download, Upload, ChevronRight, ChevronDown, Pencil, AlertTriangle } from "lucide-react";
import api, { fmtEur } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { monthLabel } from "../components/MonthlyModule";
import { ExportButton } from "../components/ExportButton";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

const MESI_LONG = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
export const F24_SCOST_MIN = 10; // scostamento significativo: > 10€

export function ScostBadge({ versato, auto, motivo, testid }) {
  if (versato == null || auto == null) return null;
  const d = versato - auto;
  if (Math.abs(d) <= F24_SCOST_MIN) return null;
  const seg = d > 0 ? "+" : "−";
  return (
    <div className="mt-1 flex flex-col gap-0.5 items-end" data-testid={testid}>
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5"><AlertTriangle size={11} /> Versato ≠ calcolo {fmtEur(auto)} · {seg}{fmtEur(Math.abs(d))}</span>
      {motivo ? <span className="text-[10px] text-muted-foreground max-w-[220px] text-right">Motivo: {motivo}</span> : <span className="text-[10px] text-amber-600">Motivo non indicato</span>}
    </div>
  );
}

export default function F24Mensili() {
  const { selectedStructure, can } = useAuth();
  const qc = useQueryClient();
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [soloDaVersare, setSoloDaVersare] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [payDlg, setPayDlg] = useState(null); // { mese, importo }
  const [scostOpen, setScostOpen] = useState(false);
  const readOnly = !can("buste_paga");

  const { data } = useQuery({
    queryKey: ["tfr_f24", year, selectedStructure],
    queryFn: async () => (await api.get("/tfr_f24/summary", { params: { anno: year, structure_id: selectedStructure } })).data,
  });
  const tot = data?.totali || {};
  const fornitori = data?.fornitori || [];
  const fornMeta = data?.fornitori_meta || {};
  const { data: mensili = [] } = useQuery({
    queryKey: ["f24_mensili", year, selectedStructure],
    queryFn: async () => (await api.get("/tfr_f24/mensili", { params: { anno: year, structure_id: selectedStructure } })).data,
  });
  const toggleMensile = async (mese, pagato, importo, motivo) => {
    try {
      const payload = { pagato, data: new Date().toISOString().slice(0, 10) };
      if (importo !== undefined && importo !== null) payload.importo = importo;
      if (motivo !== undefined) payload.motivo = motivo;
      await api.post(`/tfr_f24/mensili/${mese}/toggle`, payload);
      qc.invalidateQueries({ queryKey: ["f24_mensili"] });
    } catch { toast.error("Errore"); }
  };
  const openPayDialog = (r) => setPayDlg({ tipo: "dip", mese: r.mese, importo: String(Number(r.f24_personale || 0).toFixed(2)), auto: Number(r.f24_personale_auto || 0), motivo: r.f24_personale_motivo || "" });
  const confermaPagamento = async () => {
    if (!payDlg) return;
    const imp = payDlg.importo === "" ? 0 : Number(payDlg.importo);
    if (isNaN(imp) || imp < 0) { toast.error("Importo non valido"); return; }
    const motivo = Math.abs(imp - Number(payDlg.auto || 0)) > F24_SCOST_MIN ? (payDlg.motivo || "") : "";
    if (payDlg.tipo === "forn") {
      await toggleFornMese(payDlg.mese, true, imp, motivo);
    } else {
      await toggleMensile(payDlg.mese, true, imp, motivo);
    }
    toast.success("F24 confermato e segnato come pagato");
    setPayDlg(null);
  };
  const uploadRicevuta = async (mese, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/tfr_f24/mensili/${mese}/ricevuta`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Ricevuta caricata"); qc.invalidateQueries({ queryKey: ["f24_mensili"] }); }
    catch { toast.error("Errore caricamento"); }
  };
  const downloadRicevuta = async (mese, nome) => {
    try {
      const res = await api.get(`/tfr_f24/mensili/${mese}/ricevuta/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.target = "_blank"; a.download = nome || "ricevuta";
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Errore download"); }
  };
  const versaFornitore = async (spesa_id, versato) => {
    try {
      await api.post("/tfr_f24/fornitore_versa", { spesa_id, versato, data: new Date().toISOString().slice(0, 10) });
      toast.success(versato ? "F24 fornitore versato" : "Versamento annullato");
      qc.invalidateQueries({ queryKey: ["tfr_f24"] });
    } catch { toast.error("Errore"); }
  };
  const oggi = new Date().toISOString().slice(0, 10);
  // Scadenza F24: il 16 del mese SUCCESSIVO al periodo di competenza (es. ago 2026 -> 16 set 2026)
  const scadenzaMese = (mese) => { const [y, m] = mese.split("-").map(Number); const d = new Date(y, m, 16); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-16`; };
  const MESI_SHORT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
  const in7gg = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const f24Status = (r) => {
    const scad = scadenzaMese(r.mese);
    if (!r.f24_personale || r.f24_personale <= 0.005) return "vuoto";
    if (r.pagato) return "pagato";
    if (scad < oggi) return "scaduto";
    if (scad <= in7gg) return "scadenza";
    return "dapagare";
  };
  const STATUS_CFG = {
    pagato: { lbl: "Pagato", cls: "bg-emerald-50 border-emerald-300 text-emerald-800", dot: "bg-emerald-500" },
    scaduto: { lbl: "Scaduto", cls: "bg-red-50 border-red-300 text-red-800", dot: "bg-red-500" },
    scadenza: { lbl: "In scadenza", cls: "bg-amber-50 border-amber-300 text-amber-800", dot: "bg-amber-500" },
    dapagare: { lbl: "Da pagare", cls: "bg-blue-50 border-blue-300 text-blue-800", dot: "bg-blue-500" },
    vuoto: { lbl: "Senza importo", cls: "bg-muted/40 border-border text-muted-foreground", dot: "bg-muted-foreground/40" },
  };
  const statusCount = mensili.reduce((acc, r) => { const s = f24Status(r); acc[s] = (acc[s] || 0) + 1; return acc; }, {});

  const fornPerMese = {};
  fornitori.forEach((f) => { const m = (f.data || "").slice(0, 7); if (m) (fornPerMese[m] = fornPerMese[m] || []).push(f); });
  const mesiFornitori = Array.from({ length: 12 }, (_, i) => {
    const mm = `${year}-${String(i + 1).padStart(2, "0")}`;
    const items = fornPerMese[mm] || [];
    const meta = fornMeta[mm] || {};
    const importoAuto = items.reduce((s, f) => s + f.importo, 0);
    const override = meta.importo_override;
    const importo = override != null ? override : importoAuto;
    const pagatoMese = !!meta.pagato;
    const versatoAuto = items.reduce((s, f) => s + (f.versato ? f.importo : 0), 0);
    const versato = pagatoMese ? importo : versatoAuto;
    const residuo = Math.max(0, importo - versato);
    let stato = "vuoto";
    if (importo > 0.005) stato = residuo <= 0.005 ? "versato" : (versato > 0.005 ? "parziale" : "daversare");
    return { mese: mm, items, importo, importoAuto, override, versato, residuo, stato, meta };
  });
  const toggleFornMese = async (mese, pagato, importo, motivo) => {
    try {
      const payload = { pagato, data: oggi };
      if (importo !== undefined && importo !== null) payload.importo = importo;
      if (motivo !== undefined) payload.motivo = motivo;
      await api.post(`/tfr_f24/fornitori/${mese}/toggle`, payload);
      qc.invalidateQueries({ queryKey: ["tfr_f24"] });
    } catch { toast.error("Errore"); }
  };
  const openPayForn = (m) => setPayDlg({ tipo: "forn", mese: m.mese, importo: String(Number(m.importo || 0).toFixed(2)), auto: Number(m.importoAuto || 0), motivo: m.meta.motivo || "" });
  const scostamenti = [];
  mensili.forEach((r) => { if (r.pagato && r.f24_personale_override != null) { const d = r.f24_personale_override - (r.f24_personale_auto || 0); if (Math.abs(d) > F24_SCOST_MIN) scostamenti.push({ key: `dip-${r.mese}`, mese: r.mese, tipo: "Dipendenti", versato: r.f24_personale_override, auto: r.f24_personale_auto || 0, delta: d, motivo: r.f24_personale_motivo }); } });
  mesiFornitori.forEach((m) => { if (m.meta.pagato && m.override != null) { const d = m.override - (m.importoAuto || 0); if (Math.abs(d) > F24_SCOST_MIN) scostamenti.push({ key: `forn-${m.mese}`, mese: m.mese, tipo: "Fornitori", versato: m.override, auto: m.importoAuto || 0, delta: d, motivo: m.meta.motivo }); } });
  scostamenti.sort((a, b) => a.mese.localeCompare(b.mese));
  const scostTot = scostamenti.reduce((s, x) => s + x.delta, 0);
  const uploadFornRicevuta = async (mese, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/tfr_f24/fornitori/${mese}/ricevuta`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Ricevuta caricata"); qc.invalidateQueries({ queryKey: ["tfr_f24"] }); }
    catch { toast.error("Errore caricamento"); }
  };
  const downloadFornRicevuta = async (mese, nome) => {
    try {
      const res = await api.get(`/tfr_f24/fornitori/${mese}/ricevuta/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.target = "_blank"; a.download = nome || "ricevuta";
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Errore download"); }
  };

  return (
    <div className="space-y-6" data-testid="f24-mensili-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold">F24 mensili</h1>
          <p className="text-sm text-muted-foreground">Versamenti F24 (scadenza il 16 del mese successivo): dipendenti (INPS+IRPEF) e fornitori (IVA/ritenute)</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-muted-foreground">Anno:</span>
          <select value={year} onChange={(e) => setYear(e.target.value)} className="h-9 rounded-md border px-2 text-sm font-semibold" data-testid="f24m-year">
            {[0, 1, 2, 3].map((o) => { const y = String(new Date().getFullYear() - o); return <option key={y} value={y}>{y}</option>; })}
          </select>
          <ExportButton filename={`f24-mensili-${year}`} title={`Piano F24 mensile ${year}`} subtitle={selectedStructure === "all" ? "Tutte le strutture" : ""}
            columns={[
              { header: "Mese", accessor: (r) => monthLabel(r.mese) },
              { header: "F24 personale", accessor: (r) => fmtEur(r.f24_personale) },
              { header: "F24 fornitori", accessor: (r) => fmtEur(r.f24_fornitori) },
              { header: "Totale F24", accessor: (r) => fmtEur(r.totale) },
              { header: "Pagato", accessor: (r) => (r.pagato ? `Sì ${r.data_pagamento || ""}` : "No") },
            ]} rows={mensili} color="#1f7a5c" testid="export-f24m" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="gr-card p-5 border-l-4" style={{ borderColor: "#7c3aed" }} data-testid="f24m-personale">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground"><ShieldCheck size={15} /> F24 personale (dipendenti)</div>
          <div className="text-3xl font-head font-bold text-violet-700 mt-1">{fmtEur(mensili.reduce((s, r) => s + (r.f24_personale || 0), 0))}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Totale anno {year}</div>
        </div>
        <div className="gr-card p-5 border-l-4" style={{ borderColor: "#2563eb" }} data-testid="f24m-fornitori">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground"><Building2 size={15} /> F24 fornitori (IVA/rit.)</div>
          <div className="text-3xl font-head font-bold text-blue-700 mt-1">{fmtEur(tot.f24_fornitori_residuo)}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Residuo da versare · maturato {fmtEur(tot.f24_fornitori_maturato)}</div>
        </div>
        <div className="gr-card p-5 border-l-4 bg-primary/5" style={{ borderColor: "#1f7a5c" }} data-testid="f24m-totale">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground"><ShieldCheck size={15} /> Totale F24 anno</div>
          <div className="text-3xl font-head font-bold text-primary mt-1">{fmtEur(mensili.reduce((s, r) => s + (r.totale || 0), 0))}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Personale + fornitori</div>
        </div>
      </div>

      {scostamenti.length > 0 && (
        <div className="gr-card border-l-4 border-amber-400 p-3" data-testid="f24-scostamenti-report">
          <button onClick={() => setScostOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 text-left" data-testid="f24-scostamenti-toggle">
            <span className="flex items-center gap-2 text-sm font-semibold text-amber-800"><AlertTriangle size={15} /> Scostamenti {year}: {scostamenti.length} {scostamenti.length === 1 ? "mese" : "mesi"} · totale {scostTot > 0 ? "+" : "−"}{fmtEur(Math.abs(scostTot))}</span>
            {scostOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          {scostOpen && (
            <div className="mt-3 space-y-1.5" data-testid="f24-scostamenti-list">
              {scostamenti.map((s) => (
                <div key={s.key} className="text-sm flex flex-wrap items-baseline gap-x-2 border-b last:border-0 pb-1.5" data-testid={`f24-scost-${s.key}`}>
                  <span className="font-semibold">{monthLabel(s.mese)}</span>
                  <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">{s.tipo}</span>
                  <span className="text-muted-foreground text-xs">versato {fmtEur(s.versato)} · calcolo {fmtEur(s.auto)}</span>
                  <span className={`font-bold ${s.delta > 0 ? "text-red-600" : "text-emerald-700"}`}>{s.delta > 0 ? "+" : "−"}{fmtEur(Math.abs(s.delta))}</span>
                  {s.motivo ? <span className="text-[11px] text-muted-foreground w-full">Motivo: {s.motivo}</span> : <span className="text-[11px] text-amber-600 w-full">Motivo non indicato</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="gr-card p-5" data-testid="f24m-riepilogo">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="font-head font-bold">Riepilogo anno {year} — stato F24 mese per mese</h3>
          <div className="flex items-center gap-3 flex-wrap text-[11px] font-semibold">
            {["pagato", "scadenza", "scaduto", "dapagare", "vuoto"].map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5" data-testid={`f24m-legend-${s}`}>
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${STATUS_CFG[s].dot}`} />
                {STATUS_CFG[s].lbl}{statusCount[s] ? ` (${statusCount[s]})` : ""}
              </span>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2.5">
          {mensili.map((r) => { const cfg = STATUS_CFG[f24Status(r)]; return (
            <div key={r.mese} className={`rounded-lg border p-2.5 transition-colors ${cfg.cls}`} data-testid={`f24m-chip-${r.mese}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold">{MESI_SHORT[parseInt(r.mese.slice(5), 10) - 1]}</span>
                <span className={`inline-block w-2 h-2 rounded-full ${cfg.dot}`} />
              </div>
              <div className="text-sm font-head font-bold mt-0.5">{fmtEur(r.f24_personale)}</div>
              <div className="text-[10px] font-semibold uppercase tracking-wide">{cfg.lbl}</div>
            </div>
          ); })}
        </div>
      </div>

      <div className="gr-card p-5" data-testid="f24-mensili">
        <h3 className="font-head font-bold mb-1">F24 dipendenti — da pagare il 16 del mese successivo ({year})</h3>
        <p className="text-sm text-muted-foreground mb-3">Solo dipendenti: INPS + IRPEF. I fornitori sono nella tabella sotto. Scadenza: il 16 del mese successivo (es. agosto → 16 settembre).</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b">
                <th className="py-2">Mese</th><th className="py-2 text-right">F24 dipendenti (INPS+IRPEF)</th>
                <th className="py-2 text-center">Pagato</th><th className="py-2">Ricevuta</th>
              </tr>
            </thead>
            <tbody>
              {mensili.map((r) => { const scaduto = !r.pagato && r.f24_personale > 0 && scadenzaMese(r.mese) < oggi; return (
                <tr key={r.mese} className={`border-b last:border-0 ${r.pagato ? "bg-emerald-50/50" : scaduto ? "bg-red-50" : ""}`} data-testid={`f24m-row-${r.mese}`}>
                  <td className="py-2 font-semibold">{monthLabel(r.mese)}{scaduto && <span className="ml-2 text-[10px] font-bold uppercase text-red-600" data-testid={`f24m-scaduto-${r.mese}`}>scaduto</span>}</td>
                  <td className="py-2 text-right font-bold text-primary" data-testid={`f24m-importo-${r.mese}`}>
                    {fmtEur(r.f24_personale)}
                    {r.f24_personale_override != null && Math.abs((r.f24_personale_auto || 0) - r.f24_personale_override) > 0.005 && Math.abs((r.f24_personale_auto || 0) - r.f24_personale_override) <= F24_SCOST_MIN && (
                      <div className="text-[10px] font-normal text-muted-foreground" data-testid={`f24m-modificato-${r.mese}`}>calcolo {fmtEur(r.f24_personale_auto)}</div>
                    )}
                    <ScostBadge versato={r.f24_personale_override} auto={r.f24_personale_auto} motivo={r.f24_personale_motivo} testid={`f24m-scostamento-${r.mese}`} />
                  </td>
                  <td className="py-2 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Checkbox checked={r.pagato} disabled={readOnly} onCheckedChange={(c) => { if (c) openPayDialog(r); else toggleMensile(r.mese, false); }} data-testid={`f24m-pagato-${r.mese}`} />
                      {r.pagato && r.data_pagamento && <span className="text-[11px] text-emerald-700">il {r.data_pagamento}</span>}
                      {!readOnly && r.pagato && <button onClick={() => openPayDialog(r)} className="text-muted-foreground hover:text-primary" title="Modifica importo" data-testid={`f24m-edit-${r.mese}`}><Pencil size={13} /></button>}
                    </div>
                  </td>
                  <td className="py-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {r.has_ricevuta && <Button size="sm" variant="ghost" onClick={() => downloadRicevuta(r.mese, r.ricevuta_nome)} data-testid={`f24m-dl-${r.mese}`}><Download size={14} className="mr-1" />{r.ricevuta_nome || "Ricevuta"}</Button>}
                      {!readOnly && (<>
                        <input type="file" id={`f24m-file-${r.mese}`} className="hidden" onChange={(e) => uploadRicevuta(r.mese, e.target.files?.[0])} />
                        <Button size="sm" variant="outline" onClick={() => document.getElementById(`f24m-file-${r.mese}`).click()} data-testid={`f24m-up-${r.mese}`}><Upload size={14} className="mr-1" />{r.has_ricevuta ? "Sostituisci" : "Carica"}</Button>
                      </>)}
                    </div>
                  </td>
                </tr>
              ); })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 font-bold">
                <td className="py-2">Totale anno</td>
                <td className="py-2 text-right text-primary">{fmtEur(mensili.reduce((s, r) => s + r.f24_personale, 0))}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {fornitori.length > 0 && (
        <div className="gr-card p-5" data-testid="f24-fornitori-mensili">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
            <div className="flex items-center gap-2">
              <Building2 size={18} className="text-blue-700" />
              <h3 className="font-head font-bold">F24 fornitori — IVA / ritenute d'acconto ({year})</h3>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer font-medium" data-testid="forn-solo-da-versare">
              <Checkbox checked={soloDaVersare} onCheckedChange={(c) => setSoloDaVersare(!!c)} /> Solo mesi da versare
            </label>
          </div>
          <p className="text-sm text-muted-foreground mb-3">Suddiviso per mese: totale IVA/ritenute d'acconto da versare. Clicca un mese per il dettaglio delle fatture.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[620px]">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b">
                  <th className="py-2">Mese</th><th className="py-2 text-right">Importo F24 fornitori</th><th className="py-2 text-right">Versato</th>
                  <th className="py-2 text-right">Residuo</th><th className="py-2 text-center">Pagato</th><th className="py-2">Ricevuta</th>
                </tr>
              </thead>
              <tbody>
                {mesiFornitori.filter((m) => !soloDaVersare || m.residuo > 0.005).map((m) => {
                  const isOpen = !!expanded[m.mese]; const scaduto = m.residuo > 0.005 && scadenzaMese(m.mese) < oggi;
                  return (
                  <React.Fragment key={m.mese}>
                    <tr className={`border-b last:border-0 ${m.importo > 0.005 ? "cursor-pointer hover:bg-muted/30" : ""} ${m.stato === "versato" ? "bg-emerald-50/50" : scaduto ? "bg-red-50" : ""}`}
                        onClick={() => m.importo > 0.005 && setExpanded((e) => ({ ...e, [m.mese]: !e[m.mese] }))} data-testid={`forn-mese-row-${m.mese}`}>
                      <td className="py-2 font-semibold">
                        <span className="inline-flex items-center gap-1">
                          {m.importo > 0.005 ? (isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />) : <span className="w-[15px] inline-block" />}
                          {monthLabel(m.mese)}
                          {scaduto && <span className="ml-1 text-[10px] font-bold uppercase text-red-600">scaduto</span>}
                          {m.items.length > 0 && <span className="text-[11px] text-muted-foreground font-normal">({m.items.length})</span>}
                        </span>
                      </td>
                      <td className="py-2 text-right font-bold text-blue-700">
                        {fmtEur(m.importo)}
                        {m.override != null && Math.abs((m.importoAuto || 0) - m.override) > 0.005 && Math.abs((m.importoAuto || 0) - m.override) <= F24_SCOST_MIN && (
                          <div className="text-[10px] font-normal text-muted-foreground" data-testid={`forn-mese-modificato-${m.mese}`}>calcolo {fmtEur(m.importoAuto)}</div>
                        )}
                        <ScostBadge versato={m.override} auto={m.importoAuto} motivo={m.meta.motivo} testid={`forn-scostamento-${m.mese}`} />
                      </td>
                      <td className="py-2 text-right text-emerald-700">{fmtEur(m.versato)}</td>
                      <td className="py-2 text-right font-semibold text-amber-700">{fmtEur(m.residuo)}</td>
                      <td className="py-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-2">
                          <Checkbox checked={m.stato === "versato"} disabled={readOnly || m.importo <= 0.005} onCheckedChange={(c) => { if (c) openPayForn(m); else toggleFornMese(m.mese, false); }} data-testid={`forn-mese-pagato-${m.mese}`} />
                          {m.meta.pagato && m.meta.data_pagamento && <span className="text-[11px] text-emerald-700">il {m.meta.data_pagamento}</span>}
                          {m.stato === "parziale" && <span className="text-[10px] font-semibold text-amber-700 uppercase">parziale</span>}
                          {!readOnly && m.meta.pagato && <button onClick={() => openPayForn(m)} className="text-muted-foreground hover:text-blue-700" title="Modifica importo" data-testid={`forn-mese-edit-${m.mese}`}><Pencil size={13} /></button>}
                        </div>
                      </td>
                      <td className="py-2" onClick={(e) => e.stopPropagation()}>
                        {m.importo > 0.005 && (
                          <div className="flex items-center gap-2 flex-wrap">
                            {m.meta.has_ricevuta && <Button size="sm" variant="ghost" onClick={() => downloadFornRicevuta(m.mese, m.meta.ricevuta_nome)} data-testid={`forn-mese-dl-${m.mese}`}><Download size={14} className="mr-1" />{m.meta.ricevuta_nome || "Ricevuta"}</Button>}
                            {!readOnly && (<>
                              <input type="file" id={`forn-file-${m.mese}`} className="hidden" onChange={(e) => uploadFornRicevuta(m.mese, e.target.files?.[0])} />
                              <Button size="sm" variant="outline" onClick={() => document.getElementById(`forn-file-${m.mese}`).click()} data-testid={`forn-mese-up-${m.mese}`}><Upload size={14} className="mr-1" />{m.meta.has_ricevuta ? "Sostituisci" : "Carica"}</Button>
                            </>)}
                          </div>
                        )}
                      </td>
                    </tr>
                    {isOpen && m.items.map((f) => (
                      <tr key={f.spesa_id} className="border-b last:border-0 bg-muted/20 text-[13px]" data-testid={`forn-row-${f.spesa_id}`}>
                        <td className="py-1.5 pl-7">
                          <div className="font-medium">{f.fornitore} <span className="text-xs text-muted-foreground">· {f.data}</span></div>
                          {f.descrizione && <div className="text-xs text-muted-foreground">{f.descrizione} — {f.structure_nome}</div>}
                        </td>
                        <td className="py-1.5 text-right text-blue-700">{fmtEur(f.importo)}</td>
                        <td className="py-1.5 text-right text-emerald-700">{f.versato ? fmtEur(f.importo) : "—"}</td>
                        <td className="py-1.5 text-right">{f.versato ? "—" : fmtEur(f.importo)}</td>
                        <td className="py-1.5 text-center">
                          {f.versato
                            ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 size={13} /> Versato {f.data_versamento ? `il ${f.data_versamento}` : ""}</span>
                            : <span className="text-xs font-semibold text-amber-700">Da versare</span>}
                        </td>
                        <td className="py-1.5 text-right">
                          {!readOnly && (f.versato
                            ? <Button size="sm" variant="ghost" onClick={() => versaFornitore(f.spesa_id, false)} data-testid={`forn-undo-${f.spesa_id}`}><Undo2 size={14} className="mr-1" /> Annulla</Button>
                            : <Button size="sm" variant="outline" onClick={() => versaFornitore(f.spesa_id, true)} data-testid={`forn-versa-${f.spesa_id}`}>Segna versato</Button>)}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ); })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 font-bold">
                  <td className="py-2">Totale anno</td>
                  <td className="py-2 text-right text-blue-700">{fmtEur(tot.f24_fornitori_maturato)}</td>
                  <td className="py-2 text-right text-emerald-700">{fmtEur(tot.f24_fornitori_pagato)}</td>
                  <td className="py-2 text-right text-amber-700">{fmtEur(tot.f24_fornitori_residuo)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!payDlg} onOpenChange={(o) => { if (!o) setPayDlg(null); }}>
        <DialogContent className="max-w-sm" data-testid="f24m-pay-dialog">
          <DialogHeader><DialogTitle>Conferma pagamento F24 {payDlg?.tipo === "forn" ? "fornitori" : "dipendenti"}{payDlg ? ` · ${monthLabel(payDlg.mese)}` : ""}</DialogTitle>
            <DialogDescription>Controlla e, se serve, correggi l'importo effettivamente versato prima di confermare.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Importo F24 {payDlg?.tipo === "forn" ? "fornitori" : "dipendenti"} (€)</label>
              <input type="number" step="0.01" value={payDlg?.importo ?? ""} autoFocus
                onChange={(e) => setPayDlg((p) => ({ ...p, importo: e.target.value }))}
                className="mt-1 w-full border-2 rounded-md px-3 py-2 text-lg font-semibold tabnum focus:border-primary outline-none"
                data-testid="f24m-pay-importo" />
              <div className="text-[11px] text-muted-foreground mt-1">Calcolo automatico (preventivato): <b>{fmtEur(payDlg?.auto || 0)}</b></div>
            </div>
            {payDlg && Math.abs((payDlg.importo === "" ? 0 : Number(payDlg.importo)) - Number(payDlg.auto || 0)) > F24_SCOST_MIN && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-2" data-testid="f24m-pay-scostamento">
                <div className="flex items-center gap-1.5 text-xs font-bold text-amber-800"><AlertTriangle size={13} /> Scostamento di {fmtEur(Math.abs((payDlg.importo === "" ? 0 : Number(payDlg.importo)) - Number(payDlg.auto || 0)))} rispetto al calcolo</div>
                <div>
                  <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Motivo dello scostamento</label>
                  <textarea rows={2} value={payDlg.motivo || ""} onChange={(e) => setPayDlg((p) => ({ ...p, motivo: e.target.value }))}
                    placeholder="Es. conguaglio, credito compensato, ravvedimento…"
                    className="mt-1 w-full border rounded-md px-2.5 py-1.5 text-sm focus:border-primary outline-none" data-testid="f24m-pay-motivo" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDlg(null)} data-testid="f24m-pay-cancel">Annulla</Button>
            <Button onClick={confermaPagamento} data-testid="f24m-pay-confirm">Conferma e segna pagato</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}